import React, { useState, useRef, useContext } from 'react';
import {
  Card, CardContent, Typography, Button, MenuItem, Box,
  FormControl, InputLabel, Select, Alert, CircularProgress,
  FormHelperText, Divider
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { AuthContext } from '../context/AuthContext';
import { documentsApi, DOCUMENT_TYPES, DOCUMENT_TYPE_LABELS } from '../api/documents';

export default function UploadDocument({ bunkeringId, onUploadSuccess, disabled = false }) {
  const { user } = useContext(AuthContext);
  const fileInputRef = useRef(null);

  const [docType, setDocType] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [success, setSuccess] = useState(false);

  // Available document types (exclude BDN as it's already uploaded when creating bunkering)
  const availableDocTypes = [
    DOCUMENT_TYPES.PoS,
    DOCUMENT_TYPES.PoC,
    DOCUMENT_TYPES.ANNEX
  ];

  // Check if user can upload (Shipowner or Supplier)
  const canUpload = user && (user.role === 'SHIPOWNER' || user.role === 'SUPPLIER') && !disabled;

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      // Validate file type
      if (selectedFile.type !== 'application/pdf') {
        setUploadError('Please select a PDF file.');
        return;
      }
      
      // Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (selectedFile.size > maxSize) {
        setUploadError('File size must be less than 10MB.');
        return;
      }

      setFile(selectedFile);
      setUploadError('');
      setSuccess(false);
    }
  };

  const handleUpload = async () => {
    if (!file || !docType) {
      setUploadError('Please select a document type and file.');
      return;
    }

    setUploading(true);
    setUploadError('');

    try {
      const response = await documentsApi.uploadDocument(bunkeringId, file, docType);
      
      setSuccess(true);
      setFile(null);
      setDocType('');
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Notify parent component
      if (onUploadSuccess) {
        onUploadSuccess(response.data.data);
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);

    } catch (err) {
      console.error('Upload failed:', err);
      if (err.response && err.response.data) {
        const errorData = err.response.data;
        setUploadError(errorData.detail || errorData.title || 'Upload failed. Please try again.');
      } else {
        setUploadError('Upload failed. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setDocType('');
    setUploadError('');
    setSuccess(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!canUpload) {
    return (
      <Alert severity="info">
        {disabled 
          ? 'Document upload is not available at this time.'
          : 'You do not have permission to upload documents.'
        }
      </Alert>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadFileIcon />
          Upload Additional Document
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Add supporting documents (PoS, PoC, or regulatory annexes) to this bunkering event.
        </Typography>

        {uploadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {uploadError}
          </Alert>
        )}

        {success && (
          <Alert 
            severity="success" 
            sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
            icon={<CheckCircleIcon />}
          >
            Document uploaded successfully and submitted to blockchain!
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Document Type Selection */}
          <FormControl required fullWidth>
            <InputLabel>Document Type</InputLabel>
            <Select
              value={docType}
              label="Document Type"
              onChange={(e) => setDocType(e.target.value)}
              disabled={uploading}
            >
              {availableDocTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {DOCUMENT_TYPE_LABELS[type]}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Select the type of document you're uploading
            </FormHelperText>
          </FormControl>

          {/* File Upload */}
          <Box>
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              ref={fileInputRef}
              disabled={uploading}
            />
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              fullWidth
              sx={{ mb: 1 }}
            >
              Choose PDF File
            </Button>
            
            {file && (
              <Typography variant="body2" color="text.secondary">
                Selected: {file.name} ({(file.size / (1024 * 1024)).toFixed(2)} MB)
              </Typography>
            )}
            
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Maximum file size: 10MB. Only PDF files are accepted.
            </Typography>
          </Box>

          <Divider />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={uploading || (!file && !docType)}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
              onClick={handleUpload}
              disabled={uploading || !file || !docType}
            >
              {uploading ? 'Uploading...' : 'Upload Document'}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}